import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { Container } from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import useNavigator from "@saleor/hooks/useNavigator";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { SearchBrandsPrivate_search_edges_node } from "@saleor/searches/types/SearchBrandsPrivate";
import { SearchClusters_search_edges_node } from "@saleor/searches/types/SearchClusters";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import { TenantInStoreCancelConfig } from "@saleor/store/types/InStoreCancelConfig";
import { PaymentFeeRate } from "@saleor/store/types/PaymentFeeRate";
import { FetchMoreProps } from "@saleor/types";
import { utcToLocal } from "@saleor/utils/formatters/convertTime";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import Moment from "moment";
import React, { useState } from "react";

import { maybe } from "../../../misc";
import {
  SmopRentalTypeEnum,
  SmopStatusEnum,
  StoreTypeEnum,
  SubTenantTypeEnum
} from "../../../types/globalTypes";
import { storeListPath } from "../../urls";
import StoreEditForm from "../StoreEditForm/StoreEditForm";
import { hasNoError, useValidation } from "./StoreEditValidation";

export interface StoreEditPageFormData {
  authorizeEmailAddress: string;
  authorizeMobileNum: string;
  acctName: string;
  acctNum: string;
  bankDetails: any;
  bankName: string;
  bannerImage: {
    alt: string;
    url: string;
  };
  cluster: string;
  contractId: string;
  designation: string;
  financeName: string;
  financeEmailAddress: string;
  financeMobileNum: string;
  mallBranch: string;
  paymentFeeRate: string;
  sellerBrand: string;
  signatoryName: string;
  ipay88SubMid: null;
  location: string;
  isActivate: any;
  isActivateCompanyDetails: boolean;
  isExpressLongDistanceDeliveryAllowed: boolean;
  isFeatured: boolean;
  isSameDayDeliveryAllowed: boolean;
  operationEndTime: string;
  operationStartTime: string;
  promoRibbonDescription: string;
  promoRibbonEnd: string;
  promoRibbonStart: string;
  storeName: string;
  storeType: any;
  tinNo: string;
  rentalType: SmopRentalTypeEnum;
  isUseTenantCluster: boolean;
  subTenantType: SubTenantTypeEnum | null;
  isInMallPickup: boolean;
  isInStorePickup: boolean;
  isMallStandardDeliveryEnabled: boolean;
  isStandardDeliveryEnabled: boolean;
}

const initialErrorValidation = {
  acctName: null,
  acctNum: null,
  authorizeEmailAddress: null,
  authorizeMobileNum: null,
  bankName: null,
  cluster: null,
  contractId: null,
  designation: null,
  financeEmailAddress: null,
  financeMobileNum: null,
  financeName: null,
  ipay88SubMid: null,
  location: null,
  mallBranch: null,
  paymentFeeRate: null,
  rentalType: null,
  sellerBrand: null,
  signatoryName: null,
  subTenantType: null
};

export interface StoreEditPageProps {
  /** Form actions */
  onSubmit: (data: StoreEditPageFormData, image: File) => void;
  onDelete: () => void;
  discardButton: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAutoCancelConfigModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

  /** Form state */
  disable: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  hasManageTenantPermission: boolean;

  /** Data */
  storeData: any;
  brands: SearchBrandsPrivate_search_edges_node[];
  malls: SearchMalls_search_edges_node[];
  clusterList: SearchClusters_search_edges_node[];
  paymentFeeRateChoices: PaymentFeeRate;
  autoCancelConfig: TenantInStoreCancelConfig["tenantInStoreCancelConfig"];

  /** Fetch actions */
  fetchMalls: (query: string) => void;
  fetchBrands: (query: string) => void;
  fetchClusters: (query: string) => void;
  fetchMoreMalls?: FetchMoreProps;
  fetchMoreBrands?: FetchMoreProps;
  fetchMoreClusters?: FetchMoreProps;
}

const StoreEditPage: React.FC<StoreEditPageProps> = props => {
  const {
    onSubmit,
    onDelete,
    discardButton,
    setIsAutoCancelConfigModalOpen,

    /** Form state */
    disable,
    saveButtonBarState,
    hasManageTenantPermission,

    /** Data */
    storeData,
    brands,
    malls,
    clusterList,
    paymentFeeRateChoices,
    autoCancelConfig,

    /** Fetch actions */
    fetchMalls,
    fetchBrands,
    fetchClusters,
    fetchMoreMalls,
    fetchMoreBrands,
    fetchMoreClusters
  } = props;
  const navigate = useNavigator();

  const [imageToUpload, setImageToUpload] = useState(null);

  const [errorValidation, setErrorValidation] = useState(
    initialErrorValidation
  );

  const [clusterDisplayName, setClusterDisplayName] = useStateFromProps(
    maybe(() => storeData.cluster.name, "")
  );

  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps(
    maybe(() => storeData.brand.name, "")
  );

  const [mallDisplayName, setMallDisplayName] = useStateFromProps(
    maybe(() => storeData.mall.name, "")
  );

  const checkTenantType = tenant => {
    let result = null;

    if (tenant === "Food and Beverages") {
      result = StoreTypeEnum.FOOD_AND_BEVERAGES;
    } else if (tenant === "General Merchandise") {
      result = StoreTypeEnum.GENERAL_MERCHANDISE;
    } else {
      result = StoreTypeEnum.GROCERY;
    }

    return result;
  };
  const [tenantStoreType, setTenantStoreType] = useStateFromProps(
    checkTenantType(storeData?.tenantType)
  );

  const [rentTypeDisplay, setRentTypeDisplay] = useStateFromProps(
    maybe(() => storeData.rentalType, "")
  );

  const [subTenantTypeDisplay, setSubTenantTypeDisplay] = useStateFromProps(
    maybe(() => storeData.subTenantType, "")
  );

  const [paymentRateDisplay, setPaymentRateDisplay] = useState("");

  const clusterChoices = maybe(
    () =>
      clusterList.map(cluster => ({
        label: cluster?.name,
        value: cluster?.id
      })),
    []
  );

  const sellerBrandChoices = maybe(
    () =>
      brands.map(brand => ({
        label: brand?.name + " - " + brand.legalName,
        value: brand?.id
      })),
    []
  );

  const storeType = maybe(
    () =>
      brands.map(brand => ({
        label: brand.storeType,
        value: brand?.id
      })),
    []
  );

  const mallChoices = maybe(
    () =>
      malls.map(mall => ({
        label: mall?.name,
        value: mall?.id
      })),
    []
  );

  const rentTypeChoices = [
    { label: " Fixed ", value: SmopRentalTypeEnum.FIXED },
    { label: " Percentage ", value: SmopRentalTypeEnum.PERCENTAGE }
  ];

  const subTenantChoices = [
    { label: "Food Court", value: SubTenantTypeEnum.FOOD_COURT },
    { label: "Non-Food Court", value: SubTenantTypeEnum.NON_FOOD_COURT }
  ];

  const paymentRateChoices: Array<{
    label: string;
    value: string;
  }> = React.useMemo(() => {
    if (paymentFeeRateChoices?.paymentFee?.length) {
      return paymentFeeRateChoices.paymentFee.map(rate => ({
        label: rate.rate.toString(),
        value: rate.rate.toFixed(2)
      }));
    }

    if (storeData?.paymentFeeRate != null) {
      const rate = Number(storeData.paymentFeeRate);
      if (!isNaN(rate)) {
        return [{ label: rate.toString(), value: rate.toFixed(2) }];
      }
    }

    return [];
  }, [paymentFeeRateChoices, storeData?.paymentFeeRate]);

  React.useEffect(() => {
    if (paymentRateChoices.length) {
      setPaymentRateDisplay(paymentRateChoices[0].value);
    }
  }, [paymentRateChoices]);

  const mallPickup = maybe(() => storeData.mall.isInMallPickup, false);

  const storePickup = maybe(() => storeData.isInStorePickupEnabled, false);

  const mallStandardDelivery = maybe(
    () => storeData.mall.nextDayAvailable,
    false
  );

  const storeStandardDelivery = maybe(
    () => storeData.isStandardDeliveryEnabled,
    false
  );

  const initialForm: StoreEditPageFormData = {
    acctName: maybe(
      () =>
        !storeData.bankAccountName
          ? storeData.brand.bankAccountName
          : storeData.bankAccountName,
      ""
    ),
    acctNum: maybe(
      () =>
        !storeData.bankAccountNo
          ? storeData.brand.bankAccountNo
          : storeData.bankAccountNo,
      ""
    ),
    authorizeEmailAddress: maybe(() => storeData.signatoryEmail, ""),
    authorizeMobileNum: maybe(() => storeData.signatoryMobileNo, ""),
    bankDetails: maybe(() => storeData.brand, ""),
    bankName: maybe(
      () =>
        !storeData.bankName ? storeData.brand.bankName : storeData.bankName,
      ""
    ),
    bannerImage: maybe(() => storeData.bannerImage, ""),
    cluster: maybe(() => storeData.cluster.id, ""),
    contractId: maybe(() => storeData.contractId, ""),
    designation: maybe(() => storeData.signatoryDesignation, ""),
    financeEmailAddress: maybe(
      () =>
        !storeData.financeEmail
          ? storeData.brand.financeEmail
          : storeData.financeEmail,
      ""
    ),
    financeMobileNum: maybe(
      () =>
        !storeData.financeMobileNo
          ? storeData.brand.financeMobileNo
          : storeData.financeMobileNo,
      ""
    ),
    financeName: maybe(
      () =>
        !storeData.financeName
          ? storeData.brand.financeName
          : storeData.financeName,
      ""
    ),
    ipay88SubMid: maybe(() => storeData.ipay88SubMid, ""),
    isActivate: maybe(() => storeData.status, SmopStatusEnum.ACT),
    isActivateCompanyDetails: maybe(() => storeData.isOtherBusiness, "false"),
    isExpressLongDistanceDeliveryAllowed: maybe(
      () => storeData.isExpressLongDistanceDeliveryAllowed,
      "false"
    ),
    isFeatured: maybe(() => storeData.isFeatured, false),
    isInMallPickup: mallPickup,
    isInStorePickup: mallPickup && storePickup,
    isSameDayDeliveryAllowed: maybe(
      () => storeData.isSameDayDeliveryAllowed,
      "false"
    ),
    isMallStandardDeliveryEnabled: mallStandardDelivery,
    isStandardDeliveryEnabled: mallStandardDelivery && storeStandardDelivery,
    isUseTenantCluster: maybe(() => storeData.isUseTenantCluster, false),
    location: maybe(() => storeData.location, ""),
    mallBranch: maybe(() => storeData.mall.id),
    operationEndTime: maybe(() => storeData.operationEndTime, ""),
    operationStartTime: maybe(() => storeData.operationStartTime, ""),
    paymentFeeRate: maybe(() => storeData.paymentFeeRate, ""),
    promoRibbonDescription: maybe(() => storeData.promoRibbonDescription, ""),
    promoRibbonEnd: maybe(() => utcToLocal(storeData.promoRibbonEnd), ""),
    promoRibbonStart: maybe(() => utcToLocal(storeData.promoRibbonStart), ""),
    rentalType: maybe(() => storeData.rentalType, ""),
    sellerBrand: maybe(() => storeData.brand.id),
    signatoryName: maybe(() => storeData.signatoryName, ""),
    storeName: maybe(() => storeData.storeName, ""),
    storeType: tenantStoreType,
    subTenantType: maybe(() => storeData.subTenantType, ""),
    tinNo: maybe(() => storeData.tinNo, "")
  };

  const handleSubmit = event => {
    const validate = useValidation(event);
    if (hasNoError(validate)) {
      setErrorValidation(validate);
      event.storeType = tenantStoreType;
      event.promoRibbonStart = Moment.utc(event.promoRibbonStart).format(
        "YYYY-MM-DDTHH:mm"
      );
      event.promoRibbonEnd = Moment.utc(event.promoRibbonEnd).format(
        "YYYY-MM-DDTHH:mm"
      );
      onSubmit(event, imageToUpload);
    } else {
      setErrorValidation(validate);
    }
  };

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit, hasChanged }) => {
        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          sellerBrandChoices
        );

        const handleSelectBrandStoreType = createSingleAutocompleteSelectHandler(
          change,
          setTenantStoreType,
          storeType
        );

        const handleSelectMall = createSingleAutocompleteSelectHandler(
          change,
          setMallDisplayName,
          mallChoices
        );

        const handlePaymentRate = createSingleAutocompleteSelectHandler(
          change,
          setPaymentRateDisplay,
          paymentRateChoices
        );

        const handleRentType = createSingleAutocompleteSelectHandler(
          change,
          setRentTypeDisplay,
          rentTypeChoices
        );

        const handleSubTenantType = createSingleAutocompleteSelectHandler(
          change,
          setSubTenantTypeDisplay,
          subTenantChoices
        );

        const handleDiscardModal = () => {
          if (hasChanged) {
            discardButton(true);
          } else {
            navigate(storeListPath);
          }
        };

        const handleSelectCluster = createSingleAutocompleteSelectHandler(
          change,
          setClusterDisplayName,
          clusterChoices
        );

        const breadcrumbDirectory = [
          {
            name: "Stores",
            url: "/store"
          },
          {
            name: formData?.storeName
          }
        ];
        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={handleDiscardModal} />
              <PageHeader title={formData?.storeName}></PageHeader>
            </div>
            <StoreEditForm
              disable={disable}
              hasChange={hasChanged}
              data={formData}
              change={change}
              submit={submit}
              error={errorValidation}
              sellerDisplayValue={sellerDisplayName}
              saveButtonBarState={saveButtonBarState}
              mallDisplayValue={mallDisplayName}
              brands={sellerBrandChoices}
              malls={mallChoices}
              fetchMalls={query => fetchMalls(query)}
              fetchBrands={query => fetchBrands(query)}
              fetchMoreMalls={fetchMoreMalls}
              fetchMoreBrands={fetchMoreBrands}
              paymentRateChoices={paymentRateChoices}
              rentTypeChoices={rentTypeChoices}
              paymentRateDisplay={paymentRateDisplay}
              rentTypeDisplay={rentTypeDisplay}
              onImageUpload={setImageToUpload}
              onBrandChange={handleSelectBrand}
              onBrandChangeStoreType={handleSelectBrandStoreType}
              onMallChange={handleSelectMall}
              onPaymentChange={handlePaymentRate}
              onRentalChange={handleRentType}
              onDelete={onDelete}
              discardButton={handleDiscardModal}
              clusterDisplayValue={clusterDisplayName}
              clusters={clusterChoices}
              fetchClusters={query => fetchClusters(query)}
              fetchMoreClusters={fetchMoreClusters}
              onClusterChange={handleSelectCluster}
              subTenantChoices={subTenantChoices}
              onSubTenantTypeChange={handleSubTenantType}
              subTenantTypeDisplay={subTenantTypeDisplay}
              hasManageTenantPermission={hasManageTenantPermission}
              setIsAutoCancelConfigModalOpen={setIsAutoCancelConfigModalOpen}
              autoCancelConfig={autoCancelConfig}
            />
          </Container>
        );
      }}
    </Form>
  );
};

export default StoreEditPage;
